import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    document.title = 'Reset Password | ScopeTheMarket';
  }, []);

  // Password validation
  const hasMinLength = password.length >= 6;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValid = hasMinLength && hasLetter && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) {
      setError('Please meet all password requirements');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_BASE}/api/auth/reset-password`, {
        email,
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // No token or email - show error
  if (!token || !email) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#0B0F1A' }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl text-center"
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            border: '1px solid rgba(255,255,255,0.05)' 
          }}
        >
          <div 
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
          >
            <AlertCircle className="h-8 w-8" style={{ color: '#ef4444' }} />
          </div>
          
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
            Invalid Reset Link
          </h1>
          
          <p className="mb-6" style={{ color: '#94A3B8' }}>
            This password reset link is invalid or has expired.
          </p>
          
          <Link
            to="/forgot-password"
            className="inline-block px-6 py-3 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: '#3B82F6', color: 'white' }}
          >
            Request new reset link
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#0B0F1A' }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl text-center"
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            border: '1px solid rgba(255,255,255,0.05)' 
          }}
        >
          <div 
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
          >
            <CheckCircle className="h-8 w-8" style={{ color: '#22c55e' }} />
          </div>
          
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
            Password Reset Successfully
          </h1>
          
          <p className="mb-6" style={{ color: '#94A3B8' }}>
            Your password has been updated. You can now log in with your new password.
          </p>
          
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: '#3B82F6', color: 'white' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#0B0F1A' }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl"
        style={{ 
          backgroundColor: 'rgba(255,255,255,0.02)', 
          border: '1px solid rgba(255,255,255,0.05)' 
        }}
      >
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
          >
            <Lock className="h-8 w-8" style={{ color: '#3B82F6' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
            Create new password
          </h1>
          <p style={{ color: '#94A3B8' }}>
            Enter a new password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2" style={{ color: '#94A3B8' }}>
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 pr-12 rounded-lg text-sm outline-none transition-colors"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#F8FAFC'
                }}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#64748B' }}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Password strength indicators */}
          <div className="space-y-2">
            <div className="flex gap-1">
              <div 
                className="h-1 flex-1 rounded-full transition-colors"
                style={{ backgroundColor: hasMinLength ? '#22c55e' : 'rgba(255,255,255,0.1)' }}
              />
              <div 
                className="h-1 flex-1 rounded-full transition-colors"
                style={{ backgroundColor: hasLetter ? '#22c55e' : 'rgba(255,255,255,0.1)' }}
              />
              <div 
                className="h-1 flex-1 rounded-full transition-colors"
                style={{ backgroundColor: hasNumber ? '#22c55e' : 'rgba(255,255,255,0.1)' }}
              />
            </div>
            <div className="space-y-1 text-xs">
              <p style={{ color: hasMinLength ? '#22c55e' : '#64748B' }}>
                {hasMinLength ? '✓' : '○'} At least 6 characters
              </p>
              <p style={{ color: hasLetter ? '#22c55e' : '#64748B' }}>
                {hasLetter ? '✓' : '○'} Contains a letter
              </p>
              <p style={{ color: hasNumber ? '#22c55e' : '#64748B' }}>
                {hasNumber ? '✓' : '○'} Contains a number
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2" style={{ color: '#94A3B8' }}>
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: `1px solid ${confirmPassword && !passwordsMatch ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: '#F8FAFC'
              }}
              placeholder="Confirm new password"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="mt-1 text-xs" style={{ color: '#f87171' }}>
                Passwords do not match
              </p>
            )}
          </div>

          {error && (
            <div 
              className="p-3 rounded-lg text-sm"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#f87171' }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isValid}
            className="w-full py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={{ 
              backgroundColor: '#3B82F6',
              color: 'white'
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Resetting...
              </span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
